/**
 * Export Service
 * Client-side service for downloading chat content as DOCX, XLSX, or Markdown files.
 * Calls the backend /api/v1/export endpoints for DOCX/XLSX generation.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

/**
 * Get auth headers for API requests
 */
function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('bringiq_auth_token') || 'demo-token';
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Trigger a browser file download from a Blob
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Extract a reasonable title from the message content
 */
function extractTitle(content: string, fallback: string = 'Test_Cases'): string {
  // Try to find a heading
  const headingMatch = content.match(/^#\s+(.+)/m);
  if (headingMatch) return headingMatch[1].trim();

  // Try to find "Test Cases for PROJ-123" pattern
  const ticketMatch = content.match(/test\s+cases?\s+(?:for\s+)?([A-Z]+-\d+)/i);
  if (ticketMatch) return `Test_Cases_${ticketMatch[1]}`;

  return fallback;
}

export interface ExportOptions {
  /** The markdown content from the assistant message */
  content: string;
  /** Optional source reference (e.g., JIRA ticket ID) */
  source?: string;
  /** Optional title override */
  title?: string;
  /** Optional summary text */
  summary?: string;
}

/**
 * Export content as Word (.docx) file
 */
export async function exportAsDocx(options: ExportOptions): Promise<void> {
  const title = options.title || extractTitle(options.content);

  const response = await fetch(`${API_BASE_URL}/export/docx`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      title,
      source: options.source,
      summary: options.summary,
      markdownContent: options.content,
    }),
  });

  if (!response.ok) {
    // Try to read error message from JSON response
    let errorMsg = response.statusText;
    try {
      const errBody = await response.json();
      // Handle error being either a string or an object with message property
      const errDetail = errBody.error;
      if (typeof errDetail === 'string') {
        errorMsg = errDetail;
      } else if (errDetail && typeof errDetail === 'object' && errDetail.message) {
        errorMsg = errDetail.message;
      } else if (errBody.message) {
        errorMsg = errBody.message;
      }
    } catch { /* ignore parse error */ }
    throw new Error(`Export failed (${response.status}): ${errorMsg}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('officedocument') && !contentType.includes('octet-stream')) {
    throw new Error('Server returned an unexpected response. Please try again.');
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error('Downloaded file is empty. The content may not contain exportable data.');
  }
  const filename = `${title.replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_')}.docx`;
  downloadBlob(blob, filename);
}

/**
 * Export content as Excel (.xlsx) file
 */
export async function exportAsXlsx(options: ExportOptions): Promise<void> {
  const title = options.title || extractTitle(options.content);

  const response = await fetch(`${API_BASE_URL}/export/xlsx`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      title,
      source: options.source,
      summary: options.summary,
      markdownContent: options.content,
    }),
  });

  if (!response.ok) {
    // Try to read error message from JSON response
    let errorMsg = response.statusText;
    try {
      const errBody = await response.json();
      // Handle error being either a string or an object with message property
      const errDetail = errBody.error;
      if (typeof errDetail === 'string') {
        errorMsg = errDetail;
      } else if (errDetail && typeof errDetail === 'object' && errDetail.message) {
        errorMsg = errDetail.message;
      } else if (errBody.message) {
        errorMsg = errBody.message;
      }
    } catch { /* ignore parse error */ }
    throw new Error(`Export failed (${response.status}): ${errorMsg}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('spreadsheetml') && !contentType.includes('octet-stream')) {
    throw new Error('Server returned an unexpected response. Please try again.');
  }

  const blob = await response.blob();
  if (blob.size === 0) {
    throw new Error('Downloaded file is empty. The content may not contain exportable data.');
  }
  const dateSuffix = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const safeTitle = title.replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_');
  const filename = `${safeTitle}_${dateSuffix}.xlsx`;
  downloadBlob(blob, filename);
}

/**
 * Export content as Markdown (.md) file — client-side only, no backend call needed
 */
export function exportAsMarkdown(options: ExportOptions): void {
  const title = options.title || extractTitle(options.content);
  const blob = new Blob([options.content], { type: 'text/markdown; charset=utf-8' });
  const filename = `${title.replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_')}.md`;
  downloadBlob(blob, filename);
}

export const exportService = {
  exportAsDocx,
  exportAsXlsx,
  exportAsMarkdown,
};

export default exportService;
